import { useRef, useState, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, FileSpreadsheet, Upload } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { guardarArquivo } from './arquivoPendente';

const EXTENSOES = ['xls', 'xlsx'];

/**
 * A porta de entrada de uma cotação nova: o comprador clica para escolher a
 * planilha do Compumate ou arrasta o arquivo para dentro da janela. Com o
 * arquivo em mãos, segue para a tela de importação, que já abre lendo.
 */
export default function ImportarPlanilhaDialog({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [arrastando, setArrastando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const receber = (arquivo: File | null | undefined) => {
    if (!arquivo) return;

    const extensao = arquivo.name.split('.').pop()?.toLowerCase() ?? '';
    if (!EXTENSOES.includes(extensao)) {
      setErro(`"${arquivo.name}" não é uma planilha .xls ou .xlsx — o formato que o Compumate exporta.`);
      return;
    }

    guardarArquivo(arquivo);
    navigate('/cotacoes/importar');
  };

  const soltar = (evento: DragEvent<HTMLDivElement>) => {
    evento.preventDefault();
    setArrastando(false);
    receber(evento.dataTransfer.files[0]);
  };

  return (
    <Dialog
      open={aberto}
      onOpenChange={(abrir) => {
        if (abrir) return;
        setErro(null);
        setArrastando(false);
        aoFechar();
      }}
    >
      <DialogContent
        className="max-w-[520px] p-lg"
        // A janela inteira aceita o arquivo: soltar um pouco fora da área tracejada não deve abrir a planilha no navegador.
        onDragOver={(evento) => {
          evento.preventDefault();
          setArrastando(true);
        }}
        onDragLeave={(evento) => {
          if (!evento.currentTarget.contains(evento.relatedTarget as Node | null)) setArrastando(false);
        }}
        onDrop={soltar}
      >
        <DialogTitle>Importar planilha do ERP</DialogTitle>
        <DialogDescription className="mt-xs">
          A exportação do Compumate, com um item por cor e por marca. O sistema agrupa em uma linha por produto e mostra
          tudo para conferir antes de criar a cotação.
        </DialogDescription>

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={cn(
            'mt-md flex w-full flex-col items-center justify-center gap-sm rounded-lg border-2 border-dashed px-lg py-2xl text-center transition-colors',
            arrastando ? 'border-accent bg-accent/10' : 'border-border hover:border-accent hover:bg-accent/5',
          )}
        >
          <span
            className={cn(
              'flex size-12 items-center justify-center rounded-full transition-colors',
              arrastando ? 'bg-accent text-white' : 'bg-accent/10 text-accent',
            )}
          >
            {arrastando ? <FileSpreadsheet size={22} aria-hidden="true" /> : <Upload size={22} aria-hidden="true" />}
          </span>
          <span className="text-[14px] font-bold text-ink">
            {arrastando ? 'Solte a planilha aqui' : 'Arraste a planilha para cá'}
          </span>
          <span className="text-[13px] text-muted">
            ou <span className="font-bold text-accent underline-offset-2 hover:underline">clique para escolher no computador</span>
          </span>
          <span className="text-[11px] text-muted">Arquivo .xls ou .xlsx, até 20 MB</span>
        </button>

        <input
          ref={inputRef}
          type="file"
          accept=".xls,.xlsx"
          className="sr-only"
          tabIndex={-1}
          onChange={(evento) => {
            receber(evento.target.files?.[0]);
            evento.target.value = '';
          }}
        />

        {erro && (
          <p role="alert" className="mt-md flex items-start gap-sm rounded-md border border-danger/30 bg-danger/5 px-sm py-sm text-[13px] text-danger">
            <AlertCircle size={15} className="mt-[2px] shrink-0" aria-hidden="true" />
            {erro}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
